import { Component, EventEmitter, HostBinding, Injector, Input, Output, SimpleChanges, TemplateRef } from "@angular/core";
import { AbstractControl, ControlValueAccessor, NgControl, ValidationErrors, Validator, Validators } from "@angular/forms";
import { ComponentUtility } from "@app/class/ComponentUtility";
import { TranslationService } from "@services/translation.service";
import { DropdownContainer } from "../dropdown-container/dropdown-container";
import { VSelectOptionComponent } from "./v-select-option/v-select-option.component";
import "./v-select.component.scss";

@Component({
    selector: "v-select",
    template: require("./v-select.component.html"),
    providers: [
        ComponentUtility.createValueAccessorProvider(VSelectComponent),
        ComponentUtility.createValidatorProvider(VSelectComponent)
    ]
})
export class VSelectComponent extends DropdownContainer implements ControlValueAccessor, Validator {

    @Input() selectedValue: any;
    @Output() selectedValueChange = new EventEmitter<any>();
    @HostBinding("class.required") @Input() required = false;

    @Input() label: string;
    @Input() disabled: boolean;
    @Input() icon: string;
    @Input() itemTemplate: TemplateRef<any>;

    @Input() showTooltip: boolean;

    // Create v-select-enum-option that handles this logic
    @Input() enum: any;
    @Input() enumName: string;

    // ---

    showDropdown = false;

    selectedOption: VSelectOptionComponent;
    children: VSelectOptionComponent[] = [];

    constructor(
        protected readonly injector: Injector,
        protected readonly translationService: TranslationService) {
        super();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["selectedValue"]) {
            this.setSelectedValue();
        }
    }


    toggleDropdown(event: MouseEvent): void {
        event?.stopPropagation();

        if (this.disabled) {
            this.showDropdown = false;
            return;
        }

        this.showDropdown = !this.showDropdown;
    }

    clearChildren(): void {
        this.children = [];
    }

    registerSelectOption(option: VSelectOptionComponent): void {
        this.children.push(option);

        if (option.selected || (this.selectedValue != null && this.selectedValue == option.value)) {
            this.selectedOption = option;
            option.selected = true;
        }
    }

    deregisterSelectOption(option: VSelectOptionComponent): void {
        const indexOfChild: number = this.children.indexOf(option);

        if (indexOfChild === -1) {
            return;
        }

        this.children.splice(indexOfChild, 1);
    }

    itemClicked(item: VSelectOptionComponent): void {
        this.showDropdown = false;

        if (this.disabled) {
            return;
        }

        this.selectedOption = item;

        this.clearSelectedValue();

        this.selectedOption.selected = true;

        this.selectedValueChange.emit(this.selectedOption.value);

        if (this.onChange) {
            this.onChange(this.selectedOption.value);
        }
    }

    onClosed($event: MouseEvent): void {
        this.markAsTouched();
        setTimeout(() => {
            this.showDropdown = false;            
        });
    }

    // --- Control Value Accessor

    onChange: (value: any) => void;
    onTouched: () => void;
    control: NgControl;
    
    registerOnChange(callback: (value: any) => void): void {
        this.onChange = callback;
    }

    registerOnTouched(callback: () => void): void {
        this.onTouched = callback;
    }

    writeValue(value: string): void {
        this.selectedValue = value;
        this.setSelectedValue();
    }

    setDisabledState(state: boolean): void {
        this.disabled = state;
    }

    markAsTouched(): void {
        if (!this.onTouched || !this.control || this.control.touched) {
            return;
        }

        this.onTouched();
    }
    // Validator

    validate(control: AbstractControl): ValidationErrors | null {
        this.required = control.hasValidator(Validators.required);

        this.control = this.injector.get(NgControl);

        return null;
    }


    // ---

    private setSelectedValue(): void {
        this.clearSelectedValue();

        const matchingValue = this.children.find(option => option.value === this.selectedValue);

        if (matchingValue) {
            matchingValue.selected = true;
            return;
        }

        delete this.selectedOption;
    }

    private clearSelectedValue(): void {
        this.children.forEach(option => option.selected = false);
    }
}
